import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useAnimationSettings } from "@/components/animation/AnimationSettings";
import { personalizationApi } from "@/api/personalizationClient";
import ChyronTicker from "@/components/animation/ChyronTicker";

/**
 * Personalized chyron ticker — fetches the user's adaptive ticker items from
 * /api/oracle/personalization/chyron and renders them via the existing
 * ChyronTicker (premium motion + reduced-motion compliant).
 *
 * Falls back to the base ChyronTicker when no items are returned (guests).
 */
export default function PersonalizedChyronTicker({
  testid = "personalized-chyron-ticker",
  height = 30,
  sticky = false,
}) {
  const { prefs } = useAnimationSettings();
  const [items, setItems] = useState(null);
  const [archetype, setArchetype] = useState("");

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await personalizationApi.chyron();
      if (data?.items?.length) {
        setItems(data.items);
        setArchetype(data.archetype || "");
      } else {
        setItems(null);
      }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Refresh when auth changes (login/logout) and at a slow interval to pick
  // up profile edits performed elsewhere in the app.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onAuth = () => fetchItems();
    window.addEventListener("carelogix:auth-changed", onAuth);
    const id = setInterval(fetchItems, 60_000);
    return () => { window.removeEventListener("carelogix:auth-changed", onAuth); clearInterval(id); };
  }, [fetchItems]);

  const node = useMemo(() => {
    if (!items) return <ChyronTicker testid={`${testid}-fallback`} height={height} sticky={sticky} />;
    return <ChyronTicker items={items} testid={`${testid}-active`} height={height} sticky={sticky} />;
  }, [items, height, sticky, testid]);

  return (
    <motion.div
      style={{ width: "100%", minWidth: 0 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: prefs?.reduceMotion ? 0 : 0.3 }}
      data-testid={testid}
      aria-label={archetype ? `Personalized chyron · ${archetype}` : "CareLogix ORACLE chyron"}
    >
      {node}
    </motion.div>
  );
}
